import { supabase } from "@/integrations/supabase/client";
import {
  API_BASE_URL,
  PROPERTY_SLUG,
  CALENDAR_CACHE_TTL_MS,
  getActiveProperty,
} from "./config";
import { getAttribution } from "@/lib/analytics/dataLayer";
import { applyCabanaFallback } from "./sameDayCabanaFallback";
import type {
  AggregateChildrenResponse,
  AvailabilityBoundsResponse,
  AvailabilityResponse,
  BookingRequest,
  BookingResponse,
  CalendarDay,
  CalendarResponse,
  DiscountValidationResponse,
  PackagePricingResponse,
} from "./types";

/**
 * Multi-night availability bounds via Lovable Cloud edge function.
 * Geronimo-identical response shape.
 */
export async function fetchAvailabilityBounds(
  checkin: string,
): Promise<AvailabilityBoundsResponse> {
  const { data, error } = await supabase.functions.invoke<AvailabilityBoundsResponse>(
    "availability-bounds",
    { body: { property: getActiveProperty(), checkin } },
  );
  if (error) throw error;
  if (!data) throw new Error("Empty bounds response");
  return data;
}

interface CacheEntry {
  ts: number;
  data: Record<string, CalendarDay>;
}

const calendarCache = new Map<string, CacheEntry>();

// The upstream Railway pricing API allows this domain via CORS and exposes
// the booking endpoints publicly — no API key or auth header is required.
export async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) ?? {}),
  };
  return fetch(`${API_BASE_URL}${path}`, { ...options, headers });
}

function monthKey(d: string) {
  // d is yyyy-MM-dd
  return d.slice(0, 7);
}

/** All YYYY-MM month keys covered by [start, end] inclusive. */
function monthsBetween(start: string, end: string): string[] {
  const out: string[] = [];
  const [sy, sm] = start.slice(0, 7).split("-").map(Number);
  const [ey, em] = end.slice(0, 7).split("-").map(Number);
  let y = sy;
  let m = sm;
  while (y < ey || (y === ey && m <= em)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out;
}

/**
 * Run `tasks` with at most `concurrency` running at once. Preserves order.
 * Used to cap upstream calendar fan-out so Railway's rate limiter doesn't
 * 429 the entire batch on mobile.
 */
async function runWithConcurrency<T>(
  tasks: Array<() => Promise<T>>,
  concurrency: number,
): Promise<T[]> {
  const results: T[] = new Array(tasks.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(concurrency, tasks.length) }, async () => {
    while (true) {
      const i = next++;
      if (i >= tasks.length) return;
      results[i] = await tasks[i]();
    }
  });
  await Promise.all(workers);
  return results;
}

/**
 * Map app-side category types (e.g. "cabana_a", "cabana_b", "cabana_c", "picnic")
 * to the slugs the pricing middleware accepts on /api/availability-calendar.
 * Per-area cabana types are not recognized by the calendar endpoint and
 * return every day as `closed`, so we fall back to the aggregate "cabana".
 */
function mapCalendarCategory(category: string): string {
  if (category === "cabana_a" || category === "cabana_b") return "cabana";
  if (category === "cabana_c") return "cabana_no_utilities";
  // Son's Island Day Cabana subtypes — both roll up to the aggregate
  // `cabana` slug on the sons-island property calendar endpoint.
  if (category === "island_cabana" || category === "key_cabana") return "cabana";
  if (category === "picnic") return "picnic_table";
  if (category === "tubing") return "wristband";
  return category;
}

export async function fetchAvailabilityCalendar(
  start: string,
  end: string,
  category?: string,
  signal?: AbortSignal,
): Promise<Record<string, CalendarDay>> {
  // Son's Island day cabanas: both `island_cabana` and `key_cabana` roll up
  // to the aggregate `cabana` slug (mapped by `mapCalendarCategory`). We
  // previously fetched each pool by hardcoded `item_ids` and merged, but
  // that list went stale whenever new cabanas were added in the Hub —
  // those days then reported `sold_out` even though inventory existed.
  // The aggregate `categories=cabana` endpoint reflects all current
  // cabana inventory and returns the true lowest price.
  // The Geronimo property endpoint returns `lowest_price: null` and incorrect
  // statuses when a `categories` filter is passed — pricing/availability only
  // resolve correctly on the aggregate (un-filtered) calendar. Match the
  // behavior of Son's Geronimo's own booking page by omitting the filter
  // whenever we're targeting that property.
  const activeProperty = getActiveProperty();
  const mappedCategory =
    activeProperty === "geronimo"
      ? undefined
      : category && category.length > 0
        ? mapCalendarCategory(category)
        : undefined;
  const catKey = mappedCategory ?? "all";
  const now = Date.now();
  const months = monthsBetween(start, end);
  const cacheKeyFor = (mk: string) => `${getActiveProperty()}:${catKey}:${mk}`;

  // Cache hit: if every month covered by [start, end] is present and fresh,
  // skip the network entirely. Each month is stored under its own key so
  // overlapping windows can reuse partial data across calls.
  const merged: Record<string, CalendarDay> = {};
  let allFresh = true;
  for (const mk of months) {
    const c = calendarCache.get(cacheKeyFor(mk));
    if (c && now - c.ts < CALENDAR_CACHE_TTL_MS) {
      Object.assign(merged, c.data);
    } else {
      allFresh = false;
      break;
    }
  }
  if (allFresh) return merged;

  // Upstream `/api/availability-calendar` has a bug where wide multi-month
  // windows return every day as `sold_out` for some categories (notably
  // `tent_camping`). Per-month sub-windows return correct data. When the
  // requested window spans more than one calendar month, fan out per-month
  // fetches in parallel and merge — the recursive single-month calls reuse
  // the same cache + in-flight dedup.
  if (months.length > 1) {
    const results = await runWithConcurrency(
      months.map((mk) => () => {
        const [y, m] = mk.split("-").map(Number);
        const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
        const mStart = `${mk}-01`;
        const mEnd = `${mk}-${String(lastDay).padStart(2, "0")}`;
        // Clamp first/last month to the requested range so callers that ask
        // for a partial month still get only what they asked for.
        const reqStart = mk === months[0] ? start : mStart;
        const reqEnd = mk === months[months.length - 1] ? end : mEnd;
        return fetchAvailabilityCalendar(reqStart, reqEnd, category, signal);
      }),
      2,
    );
    const out: Record<string, CalendarDay> = {};
    for (const r of results) Object.assign(out, r);
    return out;
  }

  let url = `/api/availability-calendar?property=${getActiveProperty()}&start=${start}&end=${end}`;
  if (mappedCategory) {
    url += `&categories=${encodeURIComponent(mappedCategory)}`;
  }
  // In-flight dedup keyed by exact window so concurrent identical fetches
  // (e.g. cabana_a + cabana_b both mapping to "cabana") share one request.
  const inflightKey = `cal|${catKey}|${start}|${end}`;
  const existing = inflightRequests.get(inflightKey) as Promise<Record<string, CalendarDay>> | undefined;
  if (existing) return existing;
  const p = (async () => {
    const resp = await apiFetch(url, { signal });
    if (!resp.ok) throw new Error(`Calendar fetch failed: ${resp.status}`);
    const json = (await resp.json()) as CalendarResponse;
    if (!json.success) throw new Error("Calendar response unsuccessful");
    // Split the response into per-month buckets so future single-month
    // requests (e.g. month nav, useDerivedBounds) hit cache instantly.
    const buckets: Record<string, Record<string, CalendarDay>> = {};
    for (const [date, day] of Object.entries(json.dates)) {
      const mk = monthKey(date);
      (buckets[mk] ??= {})[date] = day as CalendarDay;
    }
    const ts = Date.now();
    for (const mk of months) {
      const prev = calendarCache.get(cacheKeyFor(mk))?.data ?? {};
      const fresh = buckets[mk] ?? {};
      calendarCache.set(cacheKeyFor(mk), { ts, data: { ...prev, ...fresh } });
    }
    return json.dates;
  })();
  inflightRequests.set(inflightKey, p);
  p.finally(() => {
    if (inflightRequests.get(inflightKey) === p) inflightRequests.delete(inflightKey);
  }).catch(() => { /* swallow */ });
  return p;
}

function addDays(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

function diffDays(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  const da = Date.UTC(ay, am - 1, ad);
  const db = Date.UTC(by, bm - 1, bd);
  return Math.round((db - da) / 86400000);
}

/**
 * Walks every night in [checkin, checkout) and rejects if any sold_out/closed.
 * Returns { ok: true } if every night is okay or fetch failed (defer to /availability).
 */
export async function validateRangeAvailability(
  checkin: string,
  checkout: string,
): Promise<{ ok: true } | { ok: false; reason: string }> {
  try {
    const nights = diffDays(checkin, checkout);
    if (nights <= 0) return { ok: false, reason: "Check-out must be after check-in." };

    // Collect all months we need
    const months = new Set<string>();
    for (let i = 0; i < nights; i++) {
      months.add(monthKey(addDays(checkin, i)));
    }

    const monthData: Record<string, CalendarDay> = {};
    for (const mk of months) {
      const start = `${mk}-01`;
      const [y, m] = mk.split("-").map(Number);
      const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
      const end = `${mk}-${String(lastDay).padStart(2, "0")}`;
      const cal = await fetchAvailabilityCalendar(start, end);
      Object.assign(monthData, cal);
    }

    for (let i = 0; i < nights; i++) {
      const day = addDays(checkin, i);
      const info = monthData[day];
      if (!info) continue; // unknown — defer
      if (info.status === "sold_out" || info.status === "closed") {
        return {
          ok: false,
          reason: "Some dates in your selection are unavailable. Please pick a different range.",
        };
      }
    }
    return { ok: true };
  } catch {
    // Defer to /availability if calendar fails
    return { ok: true };
  }
}

// ---------------------------------------------------------------------------
// In-flight request dedup + response cache (with sessionStorage persistence
// for the pricing endpoints so a reload mid-booking doesn't re-pay the cold
// /package-pricing / /aggregate-children cost).
// ---------------------------------------------------------------------------
const RESPONSE_CACHE_TTL_MS = 90_000;
// Pricing endpoints (`pp|...`, `agg|...`) are stable enough within a booking
// flow that a longer TTL is safe and avoids redundant cold hits when the user
// steps backwards/forwards between wizard steps.
const PRICING_CACHE_TTL_MS = 5 * 60 * 1000;
const SESSION_STORAGE_PREFIX = "srr:rc:";

const inflightRequests = new Map<string, Promise<unknown>>();
const responseCache = new Map<string, { ts: number; data: unknown }>();

function isPricingCacheKey(key: string): boolean {
  return key.startsWith("pp|") || key.startsWith("agg|");
}

function cacheTtl(key: string): number {
  return isPricingCacheKey(key) ? PRICING_CACHE_TTL_MS : RESPONSE_CACHE_TTL_MS;
}

function readPersistedCache(key: string): { ts: number; data: unknown } | null {
  if (!isPricingCacheKey(key)) return null;
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts: number; data: unknown };
    if (!parsed || typeof parsed.ts !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

function writePersistedCache(key: string, entry: { ts: number; data: unknown }): void {
  if (!isPricingCacheKey(key)) return;
  try {
    sessionStorage.setItem(SESSION_STORAGE_PREFIX + key, JSON.stringify(entry));
  } catch {
    // sessionStorage may be full or unavailable — non-fatal.
  }
}

function setCache(key: string, data: unknown): void {
  const entry = { ts: Date.now(), data };
  responseCache.set(key, entry);
  writePersistedCache(key, entry);
}

function getCache(key: string): { ts: number; data: unknown } | undefined {
  let entry = responseCache.get(key);
  if (!entry) {
    const persisted = readPersistedCache(key);
    if (persisted) {
      responseCache.set(key, persisted);
      entry = persisted;
    }
  }
  return entry;
}

/**
 * `/package-pricing` and `/aggregate-children` for an aggregate package
 * return the SAME `children[]` payload. The audit showed both being fired
 * back-to-back when entering the wristbands step, each returning identical
 * 47KB bodies. Mirror successful responses to the sibling endpoint's cache
 * key so the second request becomes a cache hit.
 */
function mirrorAggregateCache(srcKey: string, data: unknown): void {
  if (!data || typeof data !== "object") return;
  const obj = data as Record<string, unknown>;
  const children = obj.children;
  if (!Array.isArray(children) || children.length === 0) return;
  if (srcKey.startsWith("pp|")) {
    const aggKey = "agg|" + srcKey.slice(3).replace("/api/package-pricing?", "/api/aggregate-children?");
    if (!responseCache.has(aggKey)) {
      setCache(aggKey, {
        success: obj.success ?? true,
        property: obj.property,
        package_id: obj.package_id,
        package_name: obj.package_name,
        children,
        package_rate: obj.package_rate ?? null,
        from_price_display: obj.from_price_display ?? null,
      });
    }
  } else if (srcKey.startsWith("agg|")) {
    const ppKey = "pp|" + srcKey.slice(4).replace("/api/aggregate-children?", "/api/package-pricing?");
    if (!responseCache.has(ppKey)) {
      setCache(ppKey, {
        success: obj.success ?? true,
        property: obj.property,
        package_id: obj.package_id,
        package_name: obj.package_name,
        children,
        package_rate: obj.package_rate ?? null,
      });
    }
  }
}

async function dedupedJsonFetch<T>(cacheKey: string, url: string): Promise<T> {
  const cached = getCache(cacheKey);
  if (cached && Date.now() - cached.ts < cacheTtl(cacheKey)) {
    return cached.data as T;
  }
  const existing = inflightRequests.get(cacheKey);
  if (existing) return existing as Promise<T>;
  const p = (async () => {
    const resp = await apiFetch(url);
    if (!resp.ok) throw new Error(`Fetch failed: ${resp.status} (${url})`);
    const json = (await resp.json()) as T;
    setCache(cacheKey, json);
    mirrorAggregateCache(cacheKey, json);
    return json;
  })();
  inflightRequests.set(cacheKey, p);
  p.finally(() => {
    if (inflightRequests.get(cacheKey) === p) inflightRequests.delete(cacheKey);
  }).catch(() => { /* swallow — caller handles */ });
  return p;
}

async function fetchAvailabilityRaw(
  checkin: string,
  checkout: string,
  discountCode?: string,
): Promise<AvailabilityResponse> {
  const params = new URLSearchParams({
    property: getActiveProperty(),
    checkin,
    checkout,
  });
  if (discountCode) params.set("discount_code", discountCode);
  const url = `/api/availability?${params.toString()}`;
  return dedupedJsonFetch<AvailabilityResponse>(`avail|${url}`, url);
}

/**
 * Availability with the same-day cabana safety net applied. Upstream's
 * category rollup drops `island_cabana` / `key_cabana` on same-day requests
 * even when Checkfront still has units; `applyCabanaFallback` rebuilds them
 * from the day-cabana package children (which stay accurate). No-op on every
 * other date, and on same-day once upstream is fixed.
 */
export async function fetchAvailability(
  checkin: string,
  checkout: string,
  discountCode?: string,
): Promise<AvailabilityResponse> {
  const data = await fetchAvailabilityRaw(checkin, checkout, discountCode);
  if (getActiveProperty() !== PROPERTY_SLUG) return data;
  return applyCabanaFallback(data, checkin, checkout, {
    fetchChildren: (packageId, ci, co) => fetchAggregateChildren(packageId, ci, co),
    fetchTemplate: (ci, co) => fetchAvailabilityRaw(ci, co),
  });
}

export async function validateDiscount(
  code: string,
  checkin: string,
  checkout: string,
): Promise<DiscountValidationResponse> {
  const normalized = code.trim().toUpperCase();
  const resp = await apiFetch(`/api/validate-discount`, {
    method: "POST",
    body: JSON.stringify({
      property: getActiveProperty(),
      code: normalized,
      checkin,
      checkout,
    }),
  });
  if (!resp.ok) return { success: false };
  return (await resp.json()) as DiscountValidationResponse;
}

export async function submitCheckoutCapture(payload: Record<string, unknown>): Promise<void> {
  try {
    await apiFetch(`/api/checkout-capture`, {
      method: "POST",
      body: JSON.stringify({
        property: getActiveProperty(),
        ...payload,
        attribution: getAttribution(),
      }),
      keepalive: true,
    });
  } catch {
    // Errors swallowed — never blocks the user
  }
}

export async function submitBooking(req: BookingRequest): Promise<BookingResponse> {
  const resp = await apiFetch(`/api/booking`, {
    method: "POST",
    body: JSON.stringify({ ...req, attribution: getAttribution() }),
  });
  const json = (await resp.json().catch(() => ({}))) as BookingResponse;
  if (!resp.ok && !json.error) {
    return { success: false, error: `Booking failed: ${resp.status}` };
  }
  return json;
}

export async function fetchAggregateChildren(
  packageId: string,
  checkin: string,
  checkout: string,
  parentCategory?: string,
): Promise<AggregateChildrenResponse> {
  const params = new URLSearchParams({
    property: getActiveProperty(),
    package_id: packageId,
    checkin,
    checkout,
  });
  if (parentCategory) params.set("parent_category", parentCategory);
  const url = `/api/aggregate-children?${params.toString()}`;
  return dedupedJsonFetch<AggregateChildrenResponse>(`agg|${url}`, url);
}

export async function fetchPackagePricing(
  packageId: string,
  checkin: string,
  checkout: string,
  parentCategory?: string,
): Promise<PackagePricingResponse> {
  const params = new URLSearchParams({
    property: getActiveProperty(),
    package_id: packageId,
    checkin,
    checkout,
  });
  if (parentCategory) params.set("parent_category", parentCategory);
  const url = `/api/package-pricing?${params.toString()}`;
  return dedupedJsonFetch<PackagePricingResponse>(`pp|${url}`, url);
}
